import { DateTime } from "luxon";
import {
  API_URL,
  CDN_URL,
  VIDEO_UPLOAD_LIMIT_IN_MEGABYTES,
  UNIT_MEGABYTE,
} from "./constants";
import axios from "axios";
import ReactS3Uploader from "react-s3-uploader/s3upload";
import { getS3SignedUrl } from "./storageUtils";
import { toast } from "react-toastify";
import { DELIVERY_FORMAT } from "../gig/common/constants";
import moment from "moment-timezone";

/**
 * Removes trailing slash if present.
 */
function trimTrailingSlash(text) {
  return text.replace(/\/+$/, "");
}

/**
 * Removes every whitespace from a string.
 * Example: " whi te space " -> "whitespace".
 */
function removeAllWhitespace(text) {
  return text.replace(/[^A-Z0-9]+/gi, "");
}

/**
 * Transform a string into title case following English rules.
 * See https://github.com/blakeembrey/change-case/tree/master/packages/title-case#readme.
 */
function toTitleCase(input) {
  const SMALL_WORDS =
    /\b(?:an?d?|a[st]|because|but|by|en|for|i[fn]|neither|nor|o[fnr]|only|over|per|so|some|tha[tn]|the|to|up|upon|vs?\.?|versus|via|when|with|without|yet)\b/i;
  const TOKENS = /[^\s:–—-]+|./g;
  const WHITESPACE = /\s/;
  const IS_MANUAL_CASE = /.(?=[A-Z]|\..)/;
  const ALPHANUMERIC_PATTERN = /[A-Za-z0-9\u00C0-\u00FF]/;

  let result = "";
  let m;

  while ((m = TOKENS.exec(input)) !== null) {
    const { 0: token, index } = m;

    if (
      // Ignore already capitalized words.
      !IS_MANUAL_CASE.test(token) &&
      // Ignore small words except at beginning or end.
      (!SMALL_WORDS.test(token) ||
        index === 0 ||
        index + token.length === input.length) &&
      // Ignore URLs.
      (input.charAt(index + token.length) !== ":" ||
        WHITESPACE.test(input.charAt(index + token.length + 1)))
    ) {
      // Find and uppercase first word character, skips over *modifiers*.
      result += token.replace(ALPHANUMERIC_PATTERN, (m) => m.toUpperCase());
      continue;
    }

    result += token;
  }

  return result;
}

const openInNewTab = (url) => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

function fromCamelToSentenceCase(text = "") {
  const result = text.replace(/([A-Z])/g, " $1");
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
}

function getSlidesToShowCount(parentSelector, childSelector, defaultWidth = 0) {
  const parent = document.querySelector(parentSelector);
  const parentWidth = parent?.clientWidth;
  const child = document.querySelector(childSelector);
  const childWidth = child?.clientWidth || defaultWidth;
  const partition = Math.floor(parentWidth / childWidth);
  if (partition) {
    return partition;
  }
  return 1;
}

/**
 *
 * @param {} event: The Talk Object
 * @returns the decided time of the talk in the format `Do, DD Mo YYYY hh:mm ZZZZ`
 */
const displayMeetTime = (event) => {
  const TalkTime = DateTime.fromISO(event["startDateTimeISO"]);
  return TalkTime.toFormat("MMM dd, T '('ZZZZ')'");
};

/**
 *
 * @param {} file The File Blob
 * @param {} setSrc The Function which will set the base64 url
 * @returns base64 string
 */
const getBase64SrcFromFile = async (file, setSrc = () => {}) => {
  if (file) {
    const reader = new FileReader();
    reader.onload = (theFile) => {
      var binaryData = theFile.target.result;
      var base64String = window.btoa(binaryData);
      setSrc(base64String);
      return base64String;
    };
    reader.readAsBinaryString(file);
  }
  return null;
};

/**
 *
 * @param {string} fullURL The Full URL to get the short link of;
 * @returns {string} shortURL The Short URL using the shortener
 */
const getShortUrl = async (fullURL) => {
  let response = null;
  try {
    response = await axios.post(`${API_URL}/v1/url/short`, {
      fullURL,
    });
  } catch (err) {
    console.debug("Error Generating Short URL. Fallback to Full URL");
  }
  let shortURL = "";
  if (response?.data?.shortURL) {
    shortURL = response.data.shortURL;
  } else {
    shortURL = fullURL;
  }
  return shortURL;
};

const getImageUrl = (url) => {
  if (url?.startsWith("public")) {
    return CDN_URL + url;
  }
  return url;
};

const addQueryParamToUrl = (url = "", paramName = "", paramValue = "") => {
  // Base case
  if (
    url?.length === 0 ||
    paramName?.length === 0 ||
    paramValue?.length === 0
  ) {
    return url;
  }

  let newUrl = url;

  if (newUrl?.includes("?")) {
    newUrl = `${newUrl}&${paramName}=${paramValue}`;
  } else {
    newUrl = `${newUrl}?${paramName}=${paramValue}`;
  }

  return newUrl;
};

export function setWithExpiry(key, value, ttl) {
  const item = {
    value: value,
    expiry: new Date().getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

export function getWithExpiry(key) {
  const itemString = window.localStorage.getItem(key);
  if (!itemString) return null;

  const item = JSON.parse(itemString);
  const isExpired = new Date().getTime() > item.expiry;

  if (isExpired) {
    localStorage.removeItem(key);
    return null;
  }

  return item.value;
}

// Source: MDN.
function getRandomIntInRange(min, max) {
  //The minimum is inclusive and the maximum is exclusive.
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min);
}

function filterFalsyValues(array) {
  if (!array) {
    return [];
  }
  return array
    .map((_element) =>
      typeof _element === "string" ? _element.trim() : _element
    )
    .filter(Boolean);
}

function getPlainTextFromDelta(delta) {
  return delta?.ops?.reduce(function (text, op) {
    if (!op.insert) return "";
    if (typeof op.insert !== "string") return text + " ";
    return text + op.insert;
  }, "");
}

function generateAvatar(name, colorNumber) {
  const initials = name
    .split(" ")
    .map(function (str, index) {
      if (index > 1) return null;
      return str ? str[0].toUpperCase() : "";
    })
    .join("");
  var canvas = document.createElement("canvas");
  //The Number is calculated such that the width and height should be 518 by maintaining the ratio 6:1 of radius and margin
  //Why 518? It's calculated by hit and trial method such that image will not look pixilated.
  const calculatedNumber = 37;
  var radius = calculatedNumber * 6;
  var margin = calculatedNumber;
  canvas.width = radius * 2 + margin * 2;
  canvas.height = radius * 2 + margin * 2;

  //Add colors
  const backgroundColors = [
    "#5F2EEA",
    "#0FC3BD",
    "#EB1771",
    "#EBBA0B",
    "#DC1D00",
  ];

  const backgroundColor = backgroundColors[parseInt(colorNumber) % 5];
  // Get the drawing context
  var ctx = canvas.getContext("2d");
  ctx.beginPath();
  ctx.arc(radius + margin, radius + margin, radius, 0, 2 * Math.PI, false);
  ctx.closePath();
  ctx.fillStyle = backgroundColor;
  ctx.fill();
  ctx.fillStyle = "white";
  //Why 5.75? It's calculated by hit and trial method such that the font of the image will be perfectly aligned.
  ctx.font = `bold ${margin * 5.75}px Arial`;
  ctx.textAlign = "center";
  ctx.fillText(initials, radius + margin, (radius * 4) / 3 + margin);
  return canvas.toDataURL();
}

function uploadVideoToS3(blobs_recorded, path, toastId, onFinishS3PutCallback) {
  const blob = new Blob(blobs_recorded, { type: "video/webm" });
  const file = new File([blob], "video.webm", { type: "video/webm" });

  const uploader = new ReactS3Uploader({
    getSignedUrl: (file, callback) => {
      if (file.size > UNIT_MEGABYTE * VIDEO_UPLOAD_LIMIT_IN_MEGABYTES) {
        toast.error(
          `Video size is greater than ${VIDEO_UPLOAD_LIMIT_IN_MEGABYTES} MBs`
        );
        return undefined;
      } else {
        getS3SignedUrl(file, callback, {
          scrubFilename: (filename) => filename.replace(/[^\w\d_\-.]+/gi, ""),
          path: path,
          isPublic: false,
          onSignedUrl: () => {
            toastId.current = toast.loading("Uploading video...");
          },
        });
      }
    },
    uploadRequestHeaders: {
      "x-amz-acl": "private",
      "Access-Control-Allow-Origin": "*",
    },
    onError: (err) => {
      toast.update(toastId.current, {
        render:
          "There was some issue while uploading your video. Please try again!",
        type: "error",
        isLoading: false,
        autoClose: true,
      });
    },
    onFinishS3Put: (publicUrl) => {
      toast.update(toastId.current, {
        render: "Video uploaded successfully!",
        type: "success",
        isLoading: false,
        autoClose: true,
      });
      onFinishS3PutCallback(publicUrl);
    },
    contentDisposition: "auto",
  });
  uploader.uploadFile(file);
}

const isRenderedInBlacklistedBrowsers = () => {
  /**
   *Here blacklistedUAIdentifier is generated using identifying the 
   longest unique common sequence of strings in the user agent of blacklisted browsers. 
   */
  const blacklistedUAIdentifier = ["Instagram", "FB"];
  return blacklistedUAIdentifier.some((identifier) =>
    window.navigator.userAgent.includes(identifier)
  );
};

const serializeToQuillDelta = (items) => {
  if (typeof items === "string") {
    return { ops: [{ insert: items }] };
  }

  if (Array.isArray(items)) {
    const listAttribute = { attributes: { list: "bullet" }, insert: "\n" };
    const ops = [];

    for (const item of items) {
      ops.push({ insert: item });
      ops.push({ ...listAttribute });
    }

    return { ops };
  }

  return items;
};

export const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const reverseString = (str) => {
  if (!str || str.length < 2 || typeof str !== "string") {
    return "Not valid";
  }

  const revArray = [];
  const length = str.length - 1;

  for (let i = length; i >= 0; i--) {
    revArray.push(str[i]);
  }

  return revArray.join("");
};

export const generateSlug = (str = "", replaceValue = "-") => {
  if (str && str.length) {
    str = str.toLowerCase(); // Convert string to lower case.
    str = str.replace(/[&\/\\#,@~`^!_*+()$~%.'":*?<>{}]/g, ""); // Remove special characters.
    str = str.replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      ""
    ); // Remove emojis.

    str = str
      .replace(/^\s+|\s+$/g, "") // Trim.
      .replace(/\s+/g, replaceValue) // Collapse whitespace and replace by -.
      .replace(/-+/g, replaceValue); // Collapse dashes.

    return str;
  }
};

export const showSkyliftTitleAccordingToType = (type) => {
  switch (type) {
    case DELIVERY_FORMAT.LIVE_CLASSROOM:
      return "class";

    case DELIVERY_FORMAT.TALK:
      return "talk";

    case DELIVERY_FORMAT.MEETUP:
      return "meetup";

    case DELIVERY_FORMAT.CLASSIFIED:
      return "classified";

    default:
      return "";
  }
};

const getConversationId = (url) => {
  const separateUrl = url.indexOf("/", url.indexOf("/") + 2);
  const parts = [url.slice(0, separateUrl), url.slice(separateUrl)];
  return parts[1];
};

const createNotificationContent = (payload) => {
  try {
    const title = payload.data.title;
    const body = payload.data.body;
    const link = payload.data.clickAction;
    if (!title) {
      throw new Error("Notification Title not found");
    }
    if (!body) {
      throw new Error("Notification Message Content not found");
    }
    if (!link) {
      throw new Error("Notification Conversation Room link not found");
    }

    return payload.data;
  } catch (error) {
    throw error;
  }
};

// Get the system timezone information
const getSystemTimezoneInfo = () => {
  const systemTimezone = moment.tz.guess();
  const offset = moment.tz(systemTimezone).utcOffset() / 60;

  const timezoneObject = {
    value: systemTimezone,
    label: `(GMT${offset >= 0 ? "+" : ""}${offset}:00) ${systemTimezone}`,
    offset: offset,
    abbrev: moment.tz(systemTimezone).format("z"),
    altName: moment.tz(systemTimezone).zoneAbbr(),
  };

  return timezoneObject;
};

// Get the system timezone value Ex:Asia/Calcutta
const getSystemTimezoneValue = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const replaceIsoPlaceholderIfExists = (textMessage, startDateTimeISO) => {
  // Replace [startDateTimeISO] placeholder with system timezone date and time
  const eventStartTime = moment(startDateTimeISO).format("LLLL");
  const timezone = Utils.getSystemTimezoneInfo()?.label;
  const eventDateTime = eventStartTime + " " + timezone;
  return textMessage.replace("[startDateTimeISO]", eventDateTime);
};

const Utils = {
  trimTrailingSlash,
  removeAllWhitespace,
  toTitleCase,
  openInNewTab,
  fromCamelToSentenceCase,
  getSlidesToShowCount,
  displayMeetTime,
  getBase64SrcFromFile,
  getShortUrl,
  getImageUrl,
  addQueryParamToUrl,
  getRandomIntInRange,
  filterFalsyValues,
  getPlainTextFromDelta,
  generateAvatar,
  uploadVideoToS3,
  isRenderedInBlacklistedBrowsers,
  serializeToQuillDelta,
  isInViewport,
  generateSlug,
  showSkyliftTitleAccordingToType,
  getConversationId,
  createNotificationContent,
  getSystemTimezoneInfo,
  getSystemTimezoneValue,
  replaceIsoPlaceholderIfExists,
};

export default Utils;
